import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from 'react';

interface ThumbnailData {
  url: string;
  loaded: boolean;
  error: boolean;
  timestamp: number;
}

interface ThumbnailCacheContextType {
  getThumbnail: (fileId: string) => ThumbnailData | undefined;
  setThumbnail: (fileId: string, data: Partial<ThumbnailData>) => void;
  preloadThumbnail: (fileId: string, url: string) => void;
  clearOldCache: () => void;
}

const ThumbnailCacheContext = createContext<
  ThumbnailCacheContextType | undefined
>(undefined);

const CACHE_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes
const MAX_CACHE_SIZE = 500; // Maximum number of cached thumbnails

export const ThumbnailCacheProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const cacheRef = useRef<Map<string, ThumbnailData>>(new Map());
  const loadingRef = useRef<Set<string>>(new Set());

  const getThumbnail = useCallback(
    (fileId: string): ThumbnailData | undefined => {
      const cached = cacheRef.current.get(fileId);
      if (cached) {
        const age = Date.now() - cached.timestamp;
        if (age < CACHE_EXPIRY_TIME) {
          return cached;
        }
        cacheRef.current.delete(fileId);
      }
      return undefined;
    },
    [],
  );

  const setThumbnail = useCallback(
    (fileId: string, data: Partial<ThumbnailData>) => {
      const existing = cacheRef.current.get(fileId);
      const updated: ThumbnailData = {
        url: data.url ?? existing?.url ?? '',
        loaded: data.loaded ?? existing?.loaded ?? false,
        error: data.error ?? existing?.error ?? false,
        timestamp: Date.now(),
      };

      cacheRef.current.set(fileId, updated);

      // Implement LRU cache eviction if size exceeds limit
      if (cacheRef.current.size > MAX_CACHE_SIZE) {
        const entries = Array.from(cacheRef.current.entries());
        entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        const toRemove = entries.slice(0, Math.floor(MAX_CACHE_SIZE * 0.2)); // Remove oldest 20%
        toRemove.forEach(([key]) => cacheRef.current.delete(key));
      }
    },
    [],
  );

  const preloadThumbnail = useCallback(
    (fileId: string, url: string) => {
      if (!url || loadingRef.current.has(fileId)) return;

      const cached = getThumbnail(fileId);
      if (cached?.loaded && cached.url === url) return;

      loadingRef.current.add(fileId);

      const img = new Image();
      img.onload = () => {
        setThumbnail(fileId, { url, loaded: true, error: false });
        loadingRef.current.delete(fileId);
      };
      img.onerror = () => {
        setThumbnail(fileId, { url, loaded: true, error: true });
        loadingRef.current.delete(fileId);
      };
      img.src = url;
    },
    [getThumbnail, setThumbnail],
  );

  const clearOldCache = useCallback(() => {
    const now = Date.now();
    const entries = Array.from(cacheRef.current.entries());
    entries.forEach(([key, value]) => {
      if (now - value.timestamp > CACHE_EXPIRY_TIME) {
        cacheRef.current.delete(key);
      }
    });
  }, []);

  const value = useMemo(
    () => ({
      getThumbnail,
      setThumbnail,
      preloadThumbnail,
      clearOldCache,
    }),
    [getThumbnail, setThumbnail, preloadThumbnail, clearOldCache],
  );

  return (
    <ThumbnailCacheContext.Provider value={value}>
      {children}
    </ThumbnailCacheContext.Provider>
  );
};

export const useThumbnailCache = () => {
  const context = useContext(ThumbnailCacheContext);
  if (!context) {
    throw new Error(
      'useThumbnailCache must be used within a ThumbnailCacheProvider',
    );
  }
  return context;
};
